/**
 * Mobile Optimization Utilities
 * Provides utilities for detecting device capabilities and optimizing animations for mobile
 */

export interface DeviceCapabilities {
  isMobile: boolean;
  isTablet: boolean;
  isTouch: boolean;
  supportsHover: boolean;
  supportsBackdropFilter: boolean;
  supportsWebP: boolean;
  hasReducedMotion: boolean;
  connectionSpeed: "slow" | "medium" | "fast";
  deviceMemory: number;
  hardwareConcurrency: number;
}

/**
 * Detect if the device is mobile based on screen size and user agent
 */
export const isMobileDevice = (): boolean => {
  if (typeof window === "undefined") return false;

  const userAgent =
    navigator.userAgent || navigator.vendor || (window as any).opera;
  const mobileRegex =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  const isMobileUA = mobileRegex.test(userAgent);
  const isMobileWidth = window.innerWidth < 768;

  return isMobileUA || isMobileWidth;
};

/**
 * Detect if the device is a tablet
 */
export const isTabletDevice = (): boolean => {
  if (typeof window === "undefined") return false;

  const userAgent =
    navigator.userAgent || navigator.vendor || (window as any).opera;
  const tabletRegex = /iPad|Android(?!.*Mobile)/i;
  const isTabletUA = tabletRegex.test(userAgent);
  const isTabletWidth = window.innerWidth >= 768 && window.innerWidth < 1024;

  return isTabletUA || isTabletWidth;
};

/**
 * Detect if the device supports touch
 */
export const isTouchDevice = (): boolean => {
  if (typeof window === "undefined") return false;

  return (
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    (navigator as any).msMaxTouchPoints > 0
  );
};

/**
 * Detect if the device supports hover interactions
 */
export const supportsHover = (): boolean => {
  if (typeof window === "undefined") return true;

  return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
};

/**
 * Detect if the browser supports backdrop-filter
 */
export const supportsBackdropFilter = (): boolean => {
  if (typeof window === "undefined") return false;

  const testElement = document.createElement("div");
  testElement.style.backdropFilter = "blur(10px)";
  (testElement.style as any).webkitBackdropFilter = "blur(10px)";

  return (
    testElement.style.backdropFilter !== "" ||
    (testElement.style as any).webkitBackdropFilter !== ""
  );
};

/**
 * Detect if the browser supports WebP images
 */
export const supportsWebP = (): Promise<boolean> => {
  if (typeof window === "undefined") return Promise.resolve(false);

  return new Promise((resolve) => {
    const webP = new Image();
    webP.onload = webP.onerror = () => {
      resolve(webP.height === 2);
    };
    webP.src =
      "data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA";
  });
};

/**
 * Detect user's motion preferences
 */
export const hasReducedMotionPreference = (): boolean => {
  if (typeof window === "undefined") return false;

  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};

/**
 * Estimate connection speed based on Network Information API
 */
export const getConnectionSpeed = (): "slow" | "medium" | "fast" => {
  if (typeof window === "undefined" || !(navigator as any).connection) {
    return "medium";
  }

  const connection =
    (navigator as any).connection ||
    (navigator as any).mozConnection ||
    (navigator as any).webkitConnection;

  if (!connection) return "medium";

  const effectiveType = connection.effectiveType;

  switch (effectiveType) {
    case "slow-2g":
    case "2g":
      return "slow";
    case "3g":
      return "medium";
    case "4g":
    default:
      return "fast";
  }
};

/**
 * Get device memory in GB (if available)
 */
export const getDeviceMemory = (): number => {
  if (typeof window === "undefined") return 4; // Default to 4GB

  return (navigator as any).deviceMemory || 4;
};

/**
 * Get number of logical processors
 */
export const getHardwareConcurrency = (): number => {
  if (typeof window === "undefined") return 4; // Default to 4 cores

  return navigator.hardwareConcurrency || 4;
};

/**
 * Get comprehensive device capabilities
 */
export const getDeviceCapabilities = async (): Promise<DeviceCapabilities> => {
  const webPSupport = await supportsWebP();

  return {
    isMobile: isMobileDevice(),
    isTablet: isTabletDevice(),
    isTouch: isTouchDevice(),
    supportsHover: supportsHover(),
    supportsBackdropFilter: supportsBackdropFilter(),
    supportsWebP: webPSupport,
    hasReducedMotion: hasReducedMotionPreference(),
    connectionSpeed: getConnectionSpeed(),
    deviceMemory: getDeviceMemory(),
    hardwareConcurrency: getHardwareConcurrency(),
  };
};

/**
 * Determine if animations should be simplified based on device capabilities
 */
export const shouldSimplifyAnimations = (
  capabilities: DeviceCapabilities
): boolean => {
  // Simplify animations if:
  // - User prefers reduced motion
  // - Device has low memory (< 2GB)
  // - Connection is slow
  // - Device has few cores (< 2)
  return (
    capabilities.hasReducedMotion ||
    capabilities.deviceMemory < 2 ||
    capabilities.connectionSpeed === "slow" ||
    capabilities.hardwareConcurrency < 2
  );
};

/**
 * Determine if parallax effects should be disabled
 */
export const shouldDisableParallax = (
  capabilities: DeviceCapabilities
): boolean => {
  // Disable parallax on:
  // - Mobile devices (performance)
  // - Touch devices (poor UX)
  // - Reduced motion preference
  // - Low-end devices
  return (
    capabilities.isMobile ||
    capabilities.isTouch ||
    capabilities.hasReducedMotion ||
    shouldSimplifyAnimations(capabilities)
  );
};

/**
 * Get optimal animation duration based on device capabilities
 */
export const getOptimalAnimationDuration = (
  baseDuration: number,
  capabilities: DeviceCapabilities
): number => {
  if (capabilities.hasReducedMotion) return 0;
  if (shouldSimplifyAnimations(capabilities)) return baseDuration * 0.5;
  return baseDuration;
};

/**
 * Get optimal parallax speed based on device capabilities
 */
export const getOptimalParallaxSpeed = (
  baseSpeed: number,
  capabilities: DeviceCapabilities
): number => {
  if (shouldDisableParallax(capabilities)) return 0;
  if (capabilities.isTablet) return baseSpeed * 0.5;
  return baseSpeed;
};

/**
 * Throttle function for scroll events
 */
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  return function (this: any, ...args: Parameters<T>) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

/**
 * Debounce function for resize events
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: NodeJS.Timeout;
  return function (this: any, ...args: Parameters<T>) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), delay);
  };
};

/**
 * Request animation frame with fallback
 */
export const requestAnimationFramePolyfill = (
  callback: FrameRequestCallback
): number => {
  if (typeof window === "undefined") return 0;

  return (
    window.requestAnimationFrame ||
    (window as any).webkitRequestAnimationFrame ||
    (window as any).mozRequestAnimationFrame ||
    function (callback: FrameRequestCallback) {
      return window.setTimeout(callback, 1000 / 60);
    }
  )(callback);
};

/**
 * Cancel animation frame with fallback
 */
export const cancelAnimationFramePolyfill = (id: number): void => {
  if (typeof window === "undefined") return;

  (
    window.cancelAnimationFrame ||
    (window as any).webkitCancelAnimationFrame ||
    (window as any).mozCancelAnimationFrame ||
    function (id: number) {
      clearTimeout(id);
    }
  )(id);
};

/**
 * Add touch event listeners with passive option for better scroll performance
 */
export const addPassiveTouchListener = (
  element: HTMLElement | Window,
  event: string,
  handler: EventListener
): void => {
  element.addEventListener(event, handler, { passive: true });
};

/**
 * Remove touch event listener
 */
export const removePassiveTouchListener = (
  element: HTMLElement | Window,
  event: string,
  handler: EventListener
): void => {
  element.removeEventListener(event, handler);
};

export default {
  isMobileDevice,
  isTabletDevice,
  isTouchDevice,
  supportsHover,
  supportsBackdropFilter,
  supportsWebP,
  hasReducedMotionPreference,
  getConnectionSpeed,
  getDeviceMemory,
  getHardwareConcurrency,
  getDeviceCapabilities,
  shouldSimplifyAnimations,
  shouldDisableParallax,
  getOptimalAnimationDuration,
  getOptimalParallaxSpeed,
  throttle,
  debounce,
  requestAnimationFramePolyfill,
  cancelAnimationFramePolyfill,
  addPassiveTouchListener,
  removePassiveTouchListener,
};
